import React from 'react';
import clsx from 'clsx';
import type { NotificationInstance } from 'antd/es/notification/interface';

import { SoundEffects, Layers, WebAudio } from 'utils/audio';

import { iconLookup } from './helpers';

export interface NotificationShowOptions {
  timeout?: number | null;
  alertIcon?: string | null;
  alertButtons?: Array<{
    target?: { resourceType?: string; resourceUid?: string };
    onClick?: (close: () => void) => void;
  }> | null;
  createAlertButtons?:
    | ((handleAlertClosed: () => void) => React.ReactNode)
    | null;
  alertId?: string | number | null;
}

export interface NotificationAlert {
  alertId: string;
  alertTime: number;
  alertType: string;
  alertIcon?: string | null;
  alertButtons?: NotificationShowOptions['alertButtons'];
  createAlertButtons?: NotificationShowOptions['createAlertButtons'];
  timeout?: number | null;
  alertHeader?: string | null;
  alertMessage: React.ReactNode | ((close: () => void) => React.ReactNode);
}

const audioByAlertId: Record<string, boolean> = {};
let alertIdCounter = 0;
const listeners = new Set<() => void>();

function notifyListeners() {
  for (const fn of listeners) {
    fn();
  }
}

const openNotifications = new Map<string, NotificationAlert>();

let api: NotificationInstance | null = null;
let intercomApi: NotificationInstance | null = null;
// rendered pixel height of the intercom notification stack, reported by
// IntercomNotificationProvider so the general stack can offset itself
let intercomStackHeight = 0;
let actionRenderer:
  | ((alert: NotificationAlert, close: () => void) => React.ReactNode)
  | null = null;

function getApiForAlert(alertType: string): NotificationInstance | null {
  if (alertType === 'intercom' && intercomApi) return intercomApi;
  return api;
}

function getNotifType(
  alertType: string,
): 'success' | 'info' | 'warning' | 'error' {
  switch (alertType) {
    case 'error':
    case 'alarmHigh':
    case 'alarmMedium':
    case 'alarmLow':
      return 'error';
    case 'warning':
      return 'warning';
    case 'success':
      return 'success';
    case 'intercom':
      return 'info';
    default:
      return 'info';
  }
}

// antd's destroy(key) synchronously invokes the notice's onClose, which routes
// back into handleClose. closingAlertIds breaks that re-entrant loop, and
// silentDestroyIds marks destroys (intercom API migration) that must only
// remove the stale visual copy without running the semantic close.
const closingAlertIds = new Set<string>();
const silentDestroyIds = new Set<string>();

function handleClose(alert: NotificationAlert) {
  const { alertId, alertType } = alert;
  if (silentDestroyIds.delete(alertId) || closingAlertIds.has(alertId)) {
    return;
  }
  closingAlertIds.add(alertId);
  if (['alarmHigh', 'alarmMedium', 'alarmLow'].includes(alertType)) {
    WebAudio.stop(SoundEffects.ALARM_NOTIFICATION, Layers.ALARM_NOTIFICATIONS);
    delete audioByAlertId[alertId];
  } else if (alertType === 'intercom') {
    WebAudio.stop(
      SoundEffects.INTERCOM_NOTIFICATION,
      Layers.INTERCOM_NOTIFICATIONS,
    );
    delete audioByAlertId[alertId];
  }
  openNotifications.delete(alertId);
  if (api) api.destroy(alertId);
  if (intercomApi) intercomApi.destroy(alertId);
  notifyListeners();
  closingAlertIds.delete(alertId);
}

function playSound(alertId: string, alertType: string) {
  if (audioByAlertId[alertId]) return;
  if (['alarmHigh', 'alarmMedium', 'alarmLow'].includes(alertType)) {
    WebAudio.play(SoundEffects.ALARM_NOTIFICATION, Layers.ALARM_NOTIFICATIONS, {
      skipIfAlreadyPlaying: true,
    });
    audioByAlertId[alertId] = true;
  } else if (alertType === 'intercom') {
    WebAudio.play(
      SoundEffects.INTERCOM_NOTIFICATION,
      Layers.INTERCOM_NOTIFICATIONS,
      { skipIfAlreadyPlaying: true },
    );
    audioByAlertId[alertId] = true;
  }
}

function openNotification(alert: NotificationAlert) {
  const {
    alertIcon,
    alertType,
    alertId,
    alertMessage,
    alertButtons,
    createAlertButtons,
    alertHeader,
    timeout: alertTimeout,
  } = alert;

  const targetApi = getApiForAlert(alertType);
  if (!targetApi) return;

  const notifType = getNotifType(alertType);
  const close = () => handleClose(alert);

  const description =
    typeof alertMessage === 'function' ? alertMessage(close) : alertMessage;

  let actions;
  if (createAlertButtons) {
    actions = createAlertButtons(close);
  } else if (alertButtons?.length && actionRenderer) {
    actions = actionRenderer(alert, close);
  } else {
    actions = undefined;
  }

  const isAlarmHigh = alertType === 'alarmHigh' || alertIcon === 'p1';
  const isAlarmMedium =
    alertType === 'alarmMedium' ||
    (alertIcon && ['p2', 'p3'].includes(alertIcon));
  const isAlarmLow =
    alertType === 'alarmLow' || (alertIcon && ['p4', 'p5'].includes(alertIcon));
  const isIntercom = alertType === 'intercom';

  let duration: number;
  if (notifType === 'error' && !alertTimeout) {
    duration = 0;
  } else if (alertTimeout) {
    duration = alertTimeout / 1000;
  } else {
    duration = 5;
  }

  targetApi.open({
    key: alertId,
    title: alertHeader || description,
    description: alertHeader ? description : undefined,
    actions,
    type: notifType,
    icon: iconLookup[alertIcon || alertType],
    placement: 'bottomRight',
    onClose: () => handleClose(alert),
    className: clsx('op-notification', `op-notification--${notifType}`, {
      'op-notification--alarmHigh': isAlarmHigh,
      'op-notification--alarmMedium': isAlarmMedium,
      'op-notification--alarmLow': isAlarmLow,
      'op-notification--intercom': isIntercom,
    }),
    duration,
    ...(!alertHeader && {
      styles: {
        title: {
          marginBottom: 0,
          fontWeight: 'normal',
        },
      },
    }),
  });

  playSound(alertId, alertType);
}

export const notificationService = {
  setApi(nextApi: NotificationInstance | null) {
    api = nextApi;
    if (api) {
      for (const alert of openNotifications.values()) {
        if (alert.alertType !== 'intercom' || !intercomApi) {
          openNotification(alert);
        }
      }
    }
  },

  setIntercomApi(nextApi: NotificationInstance | null) {
    intercomApi = nextApi;
    if (intercomApi) {
      for (const alert of openNotifications.values()) {
        if (alert.alertType === 'intercom') {
          // remove the stale copy rendered on the general stack before the
          // intercom api was mounted, without running the semantic close
          // handler so the alert stays tracked
          if (api) {
            silentDestroyIds.add(alert.alertId);
            api.destroy(alert.alertId);
            silentDestroyIds.delete(alert.alertId);
          }
          openNotification(alert);
        }
      }
    }
  },

  setActionRenderer(
    nextRenderer:
      | ((alert: NotificationAlert, close: () => void) => React.ReactNode)
      | null,
  ) {
    actionRenderer = nextRenderer;
  },

  show(
    alertType: string,
    alertMessage:
      | string
      | React.ReactNode
      | ((close: () => void) => React.ReactNode),
    alertHeader?: string | null,
    options: NotificationShowOptions = {},
  ) {
    const {
      timeout = null,
      alertIcon = null,
      alertButtons = null,
      createAlertButtons = null,
      alertId: optionAlertId,
    } = options;

    const alertId = String(
      optionAlertId ?? `alert-${++alertIdCounter}-${Date.now()}`,
    );
    const alertTime = Date.now();

    let resolvedTimeout: number | null;
    if (timeout) {
      resolvedTimeout = timeout;
    } else if (alertType === 'success') {
      resolvedTimeout = 5000;
    } else if (alertType === 'intercom') {
      resolvedTimeout = 30000;
    } else {
      resolvedTimeout = null;
    }

    const alert: NotificationAlert = {
      alertId,
      alertTime,
      alertType,
      alertIcon,
      alertButtons,
      createAlertButtons,
      alertMessage,
      alertHeader: alertHeader ?? undefined,
      timeout: resolvedTimeout,
    };

    openNotifications.set(alertId, alert);
    notifyListeners();
    openNotification(alert);
  },

  clear(id?: string | number | null) {
    if (id == null) {
      this.clearAll();
      return;
    }
    const key = String(id);
    const alert = openNotifications.get(key);
    if (alert) {
      handleClose(alert);
    }
  },

  clearByType(alertType: string) {
    const toClose: NotificationAlert[] = [];
    for (const alert of openNotifications.values()) {
      if (alert.alertType === alertType) toClose.push(alert);
    }
    for (const alert of toClose) {
      handleClose(alert);
    }
  },

  // 'all' is only used via clear() with no id (and tests); the Clear all buttons pass 'general' or 'intercom'.
  // Scoped clears destroy per alert id so intercom notices rendered on the
  // general api via the fallback stay independent; only 'all' uses the
  // blanket destroy that also clears untracked notices.
  clearAll(scope: 'all' | 'general' | 'intercom' = 'all') {
    if (scope === 'all') {
      if (api) api.destroy();
      if (intercomApi) intercomApi.destroy();
    }
    for (const alert of openNotifications.values()) {
      const isIntercom = alert.alertType === 'intercom';
      if (
        scope === 'all' ||
        (scope === 'intercom' && isIntercom) ||
        (scope === 'general' && !isIntercom)
      ) {
        handleClose(alert);
      }
    }
  },

  getOpenCount() {
    return openNotifications.size;
  },

  getGeneralOpenCount() {
    let count = 0;
    for (const alert of openNotifications.values()) {
      if (alert.alertType !== 'intercom') count++;
    }
    return count;
  },

  getIntercomOpenCount() {
    let count = 0;
    for (const alert of openNotifications.values()) {
      if (alert.alertType === 'intercom') count++;
    }
    return count;
  },

  getIntercomStackHeight() {
    return intercomStackHeight;
  },

  setIntercomStackHeight(nextHeight: number) {
    if (nextHeight === intercomStackHeight) return;
    intercomStackHeight = nextHeight;
    notifyListeners();
  },

  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};

export type NotificationShowArgs = Parameters<typeof notificationService.show>;
